import { buildProps, definePropType } from '@ll-plus/utils'

import type { SizeType } from 'ant-design-vue/es/config-provider'

import type { IconProps } from '@ll-plus/components'
import type { ExtractPropTypes } from 'vue'
import type ButtonGroup from './button-group.vue'
import type Button from './button.vue'

export const mode = ['compact', 'loose'] as const

type TriggerType = 'click' | 'hover' | 'contextmenu'
type ShapeType = 'default' | 'circle' | 'round'

export const buttonProps = buildProps({
  // 按钮类型
  type: {
    type: String,
    default: 'default',
  },

  // 是否禁用
  disabled: {
    type: Boolean,
    default: false,
  },

  // 按钮文本
  label: {
    type: String,
  },

  //  图标信息
  icon: {
    type: definePropType<IconProps>(Object),
    default: () => ({
      iconName: '',
      color: '',
      size: '',
    }),
  },

  // 图标方位
  iconDirection: {
    type: String,
    default: 'left',
  },

  // 图标和文字间距
  iconSpace: {
    type: Number,
    default: 8,
  },

  // 是否为灰色类型
  isGray: {
    type: Boolean,
    default: false,
  },

  // 是否为独占一行
  isBlock: {
    type: Boolean,
    default: false,
  },

  // 是否显示（用于按钮组）
  accessControl: {
    type: Boolean,
    default: true,
  },

  // 使用自定义样式
  useCustomStyle: {
    type: Boolean,
    default: false,
  },

  // 自定义背景
  bgColor: {
    type: String,
  },

  // 自定义文本色
  color: {
    type: String,
  },

  // 自定义悬浮背景
  hoverBgColor: {
    type: String,
  },

  // 自定义悬浮文本色
  hoverColor: {
    type: String,
  },
} as const)

// 按钮组属性
export const buttonGroupProps = buildProps({
  // 数据配置
  items: {
    type: definePropType<ButtonProps[]>(Array),
    required: true,
    default: () => [] as ButtonProps[],
  },

  // 紧凑模式 / 宽松模式
  mode: {
    type: String,
    values: mode,
    default: 'compact',
  },

  // 最多显示按钮数量
  maxCount: {
    type: Number,
    default: 0,
  },

  // 按钮尺寸
  size: {
    type: String as () => SizeType,
  },

  // 图标和文字间距
  iconSpace: {
    type: Number,
    default: 4,
  },

  // 更多文字
  moreText: {
    type: String,
    default: '',
  },

  // 更多按钮形状
  moreShape: {
    type: String as () => ShapeType,
    default: 'default',
  },

  // 更多按钮类型
  moreType: {
    type: String,
    default: 'default',
  },

  // 更多按钮图标方位
  moreIconDirection: {
    type: String,
    default: 'left',
  },

  // 更多下拉触发方式
  moreTrigger: {
    type: Array as () => TriggerType[],
    default: () => ['click'],
  },

  // 按钮图标
  moreIcon: {
    type: String,
    default: 'icon-more',
  },

  // 是否全部禁用
  disabled: {
    type: Boolean,
    default: false,
  },

  // 是否为幽灵按钮
  ghost: {
    type: Boolean,
    default: false,
  },

  // 间距大小
  spaceSize: {
    type: Number,
    default: 8,
  },

  // 按钮形状
  shape: {
    type: String as () => ShapeType,
    default: 'default',
  },

  // 是否为无边框模式 ,仅紧凑型生效
  noBorder: {
    type: Boolean,
    default: false,
  },

  // 可视区文本以文字提示方式展示
  useTip: {
    type: Boolean,
    default: false,
  },

  // 是否显示文字提示（权重比useTip大）
  showTip: {
    type: Boolean,
    default: true,
  },

  // 文字提示是否为白色背景
  isWhiteBg: {
    type: Boolean,
    default: true,
  },

  // 文字提示是否显示箭头
  arrow: {
    type: Boolean,
    default: false,
  },
} as const)

// props
export type ButtonProps = ExtractPropTypes<typeof buttonProps>
export type ButtonGroupProps = ExtractPropTypes<typeof buttonGroupProps>

// instance
export type ButtonInstance = InstanceType<typeof Button>
export type ButtonGroupInstance = InstanceType<typeof ButtonGroup>
